/* =============================================================================
 * Search Drawer (dt61y5va)
 *
 * Inline search bar trigger that opens a full-width top drawer overlay
 * with predictive search results, recent searches, and featured products.
 *
 * Desktop: full-width top drawer (not a popup below the bar).
 * Mobile: full-screen drawer with body scroll lock.
 *
 * Behaviour:
 *   1. Inline search bar is always visible on the page.
 *   2. Click/focus opens a full-width drawer from the top.
 *   3. Typing calls Spectrum.search.predictive (auto-cancel + LRU cache)
 *      with a 200ms debounce so keystrokes don't flood the API.
 *   4. Results render as a product grid; "View all" links to search page.
 *   5. Enter key navigates to the configurable search results page.
 *   6. No-results state shows the featured collection as a fallback.
 *
 * SDK dependencies (resolved lazily at call time, not at init):
 *   - Spectrum.search  (predictive, getRecent, addRecent, clearRecent)
 *   - Spectrum.money   (format)
 *   - Shopify AJAX API (/collections/{handle}/products.json)
 * ============================================================================= */

;(() => {
  if (window.__sai_dt61y5va_initialized__) return
  window.__sai_dt61y5va_initialized__ = true

  const SNIPPET_ID = 'dt61y5va'
  const TAG = 'sai-dt61y5va'
  const FEATURE_SLUG = 'search_drawer'
  const DEBOUNCE_MS = 200

  function noopTrack() {}

  function searchApi() {
    return window.Spectrum?.search ?? null
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') return ''
    try {
      return window.Spectrum.money.format(cents)
    } catch (_) {
      return ''
    }
  }

  function buildProductCard(product) {
    const img = product.featured_image || product.images?.[0] || ''
    const title = product.title || ''
    const url = product.url || `/products/${product.handle || ''}`
    const price = formatMoney(product.price)
    const compareAt =
      product.compare_at_price && product.compare_at_price > product.price
        ? formatMoney(product.compare_at_price)
        : ''

    const imgHtml = img
      ? `<div class="sai-dt61y5va__product-img-wrap"><img class="sai-dt61y5va__product-img" src="${img}" alt="${title}" loading="lazy"></div>`
      : `<div class="sai-dt61y5va__product-img-wrap"><svg class="sai-dt61y5va__product-placeholder" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg></div>`

    const priceHtml = compareAt
      ? `<div class="sai-dt61y5va__product-price">${price}<span class="sai-dt61y5va__product-compare">${compareAt}</span></div>`
      : `<div class="sai-dt61y5va__product-price">${price}</div>`

    return `<a class="sai-dt61y5va__product" href="${url}">${imgHtml}<div class="sai-dt61y5va__product-info"><div class="sai-dt61y5va__product-title">${title}</div>${priceHtml}</div></a>`
  }

  function buildFeaturedCard(product) {
    const img = product.featured_image || product.images?.[0]?.src || ''
    const title = product.title || ''
    const url = `/products/${product.handle || ''}`
    const price = product.variants?.[0]?.price
      ? formatMoney(Number(product.variants[0].price) * 100)
      : ''
    const compareAt =
      product.variants?.[0]?.compare_at_price &&
      Number(product.variants[0].compare_at_price) > Number(product.variants[0].price)
        ? formatMoney(Number(product.variants[0].compare_at_price) * 100)
        : ''

    const imgHtml = img
      ? `<div class="sai-dt61y5va__product-img-wrap"><img class="sai-dt61y5va__product-img" src="${img}" alt="${title}" loading="lazy"></div>`
      : `<div class="sai-dt61y5va__product-img-wrap"><svg class="sai-dt61y5va__product-placeholder" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg></div>`

    const priceHtml = compareAt
      ? `<div class="sai-dt61y5va__product-price">${price}<span class="sai-dt61y5va__product-compare">${compareAt}</span></div>`
      : `<div class="sai-dt61y5va__product-price">${price}</div>`

    return `<a class="sai-dt61y5va__product sai-dt61y5va__featured-card" href="${url}">${imgHtml}<div class="sai-dt61y5va__product-title">${title}</div>${priceHtml}</a>`
  }

  function buildSkeletonCard() {
    return `<div class="sai-dt61y5va__skeleton"><div class="sai-dt61y5va__skeleton-img">​</div><div class="sai-dt61y5va__skeleton-line sai-dt61y5va__skeleton-line--title">​</div><div class="sai-dt61y5va__skeleton-line sai-dt61y5va__skeleton-line--price">​</div></div>`
  }

  function buildSkeletonListItem() {
    return `<div class="sai-dt61y5va__skeleton sai-dt61y5va__skeleton--list"><div class="sai-dt61y5va__skeleton-img">​</div><div class="sai-dt61y5va__skeleton-info"><div class="sai-dt61y5va__skeleton-line sai-dt61y5va__skeleton-line--title">​</div><div class="sai-dt61y5va__skeleton-line sai-dt61y5va__skeleton-line--price">​</div></div></div>`
  }

  function buildSkeletonGrid(count, layout) {
    const builder = layout === 'list' ? buildSkeletonListItem : buildSkeletonCard
    return Array.from({ length: count }, builder).join('')
  }

  function buildRecentItem(query, showDelete) {
    const deleteBtn = showDelete
      ? `<button class="sai-dt61y5va__recent-delete" type="button" data-delete-query="${query}" aria-label="Remove"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>`
      : ''
    return `<li class="sai-dt61y5va__recent-item" data-query="${query}"><span>${query}</span>${deleteBtn}</li>`
  }

  if (!customElements.get(TAG)) {
    class SaiSearchDrawerBar extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return
        this._initialized = true
        requestAnimationFrame(() => this._init())
      }

      _init() {
        this._track = noopTrack
        this._debounceTimer = null
        this._lastQuery = ''
        this._searchSeq = 0
        this._featuredCache = null
        this._featuredPromise = null

        this._els = {
          triggerBar: this.querySelector('.sai-dt61y5va__trigger-bar'),
          triggerInput: this.querySelector('.sai-dt61y5va__trigger-input'),
          backdrop: this.querySelector('.sai-dt61y5va__backdrop'),
          panel: this.querySelector('.sai-dt61y5va__panel'),
          input: this.querySelector('.sai-dt61y5va__input'),
          clearBtn: this.querySelector('.sai-dt61y5va__clear'),
          closeBtn: this.querySelector('.sai-dt61y5va__close'),
          recent: this.querySelector('.sai-dt61y5va__recent'),
          featured: this.querySelector('.sai-dt61y5va__featured'),
          results: this.querySelector('.sai-dt61y5va__results'),
          grid: this.querySelector('.sai-dt61y5va__grid'),
          viewAll: this.querySelector('.sai-dt61y5va__view-all'),
          noResults: this.querySelector('.sai-dt61y5va__no-results'),
          loading: this.querySelector('.sai-dt61y5va__loading'),
          loadingGrid: this.querySelector('.sai-dt61y5va__loading-grid'),
        }

        if (!this._els.triggerBar) return

        this._placeholderPrefix = this.getAttribute('data-placeholder-prefix') ?? 'Search for'
        this._placeholderSuggestions = (this.getAttribute('data-placeholder-suggestions') || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
        this._placeholderAnimation = this.getAttribute('data-placeholder-animation') || 'rotate'
        this._placeholderAnimTimer = null
        this._placeholderIdx = 0
        this._searchPageUrl = this.getAttribute('data-search-page-url') || '/search'
        this._drawerDirection = this.getAttribute('data-drawer-direction') || 'top'
        this._drawerStyle = this.getAttribute('data-drawer-style') || 'fixed'
        this._searchIconPosition = this.getAttribute('data-search-icon-position') || 'left'
        this._showClearButton = this.getAttribute('data-show-clear-button') !== 'false'
        this._autoFocus = this.getAttribute('data-auto-focus') !== 'false'
        this._showResultsPreview = this.getAttribute('data-show-results-preview') !== 'false'
        this._resultsCount = Number(this.getAttribute('data-results-count')) || 8
        this._viewAllText = this.getAttribute('data-view-all-text') || 'View all'
        this._viewAllStyle = this.getAttribute('data-view-all-style') || 'link'
        this._resultsLayout = this.getAttribute('data-results-layout') || 'grid'
        this._showRecent = this.getAttribute('data-show-recent') !== 'false'
        this._recentMaxItems = Number(this.getAttribute('data-recent-max-items')) || 5
        this._showClearAll = this.getAttribute('data-show-clear-all') !== 'false'
        this._showIndividualDelete = this.getAttribute('data-show-individual-delete') !== 'false'
        this._recentTagStyle = this.getAttribute('data-recent-tag-style') || 'pill'
        this._showFeatured = this.getAttribute('data-show-featured') !== 'false'
        this._featuredTitle = this.getAttribute('data-featured-title') || 'Featured Products'
        this._featuredHandle = this.getAttribute('data-featured-handle') || 'all'
        this._featuredMaxItems = Number(this.getAttribute('data-featured-max-items')) || 8
        this._featuredViewAllUrl = this.getAttribute('data-featured-view-all-url') || ''
        this._featuredLayout = this.getAttribute('data-featured-layout') || 'grid'

        this._bindEvents()
        this._startTriggerPlaceholderAnim()
      }

      setAnalytics(track) {
        this._track = typeof track === 'function' ? track : noopTrack
      }

      _bindEvents() {
        const els = this._els

        els.triggerBar.addEventListener('click', () => this._open())
        els.triggerInput.addEventListener('focus', () => this._open())

        els.triggerInput.addEventListener('input', () => {
          if (els.triggerInput.value) {
            this._stopTriggerPlaceholderAnim()
          } else {
            this._startTriggerPlaceholderAnim()
          }
        })

        els.triggerInput.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            e.preventDefault()
            this._close()
          }
          if (e.key === 'Enter') {
            e.preventDefault()
            const q = (els.triggerInput.value || '').trim()
            if (q) {
              this._track(`${FEATURE_SLUG}:submit`, { query: q })
              const api = searchApi()
              if (api) api.addRecent(q)
              this._close()
              window.location.href = `${this._searchPageUrl}?q=${encodeURIComponent(q)}`
            }
          }
        })

        els.closeBtn.addEventListener('click', () => this._close())

        els.backdrop.addEventListener('click', (e) => {
          if (e.target === els.backdrop) this._close()
        })

        if (els.clearBtn) {
          els.clearBtn.addEventListener('click', () => {
            els.input.value = ''
            els.clearBtn.hidden = true
            this._onInputChange()
            els.input.focus()
          })
        }

        els.input.addEventListener('input', () => {
          if (els.clearBtn) els.clearBtn.hidden = !els.input.value
          this._scheduleSearch()
        })

        els.input.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            e.preventDefault()
            this._close()
          }
          if (e.key === 'Enter') {
            e.preventDefault()
            const q = (els.input.value || '').trim()
            if (q) {
              this._track(`${FEATURE_SLUG}:submit`, { query: q })
              const api = searchApi()
              if (api) api.addRecent(q)
              this._close()
              window.location.href = `${this._searchPageUrl}?q=${encodeURIComponent(q)}`
            }
          }
        })

        els.backdrop.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this._close()
        })

        els.input.addEventListener('input', () => {
          if (els.input.value) {
            this._stopPlaceholderAnim()
          } else {
            this._startPlaceholderAnim()
          }
        })

        els.grid.addEventListener('click', (e) => {
          const card = e.target.closest('.sai-dt61y5va__product')
          if (card) {
            this._track(`${FEATURE_SLUG}:product_click`, {
              query: this._lastQuery,
              product_url: card.getAttribute('href') || '',
              product_title: card.querySelector('.sai-dt61y5va__product-title')?.textContent || '',
            })
          }
        })
      }

      _isMobile() {
        return window.innerWidth <= 640
      }

      _open() {
        if (this._isOpen) return
        this._isOpen = true
        const els = this._els

        if (!this._movedToBody) {
          document.body.appendChild(els.backdrop)
          this._movedToBody = true
        }

        this.classList.add('sai-dt61y5va--open')
        this._stopTriggerPlaceholderAnim()

        els.panel.setAttribute('data-direction', this._drawerDirection)
        els.panel.setAttribute('data-style', this._drawerStyle)
        document.body.style.overflow = 'hidden'

        els.backdrop.hidden = false
        els.backdrop.classList.remove('sai-dt61y5va__backdrop--closing')

        els.input.value = els.triggerInput.value || ''
        if (els.clearBtn) els.clearBtn.hidden = !els.input.value

        this._showInitialState()
        void els.backdrop.offsetHeight
        els.backdrop.classList.add('sai-dt61y5va__backdrop--visible')

        if (this._autoFocus) els.input.focus()

        setTimeout(() => this._startPlaceholderAnim(), 0)
        this._track(`${FEATURE_SLUG}:open`, {})
      }

      _close() {
        if (!this._isOpen) return
        this._isOpen = false
        this._stopPlaceholderAnim()
        const els = this._els
        const query = this._lastQuery
        els.backdrop.classList.remove('sai-dt61y5va__backdrop--visible')
        els.backdrop.classList.add('sai-dt61y5va__backdrop--closing')
        if (this._debounceTimer) {
          clearTimeout(this._debounceTimer)
          this._debounceTimer = null
        }
        this.classList.remove('sai-dt61y5va--open')
        els.triggerInput.blur()
        if (!els.triggerInput.value) this._startTriggerPlaceholderAnim()
        const onEnd = () => {
          els.backdrop.removeEventListener('transitionend', onEnd)
          els.backdrop.classList.remove('sai-dt61y5va__backdrop--closing')
          els.backdrop.hidden = true
          document.body.style.overflow = ''
          this._hideAll()
        }
        els.backdrop.addEventListener('transitionend', onEnd, { once: true })
        setTimeout(onEnd, 300)
        if (query) {
          this._track(`${FEATURE_SLUG}:close`, { query })
        }
      }

      _hideAll() {
        const els = this._els
        els.recent.hidden = true
        els.featured.hidden = true
        els.loading.hidden = true
        els.results.hidden = true
        els.noResults.hidden = true
      }

      _scheduleSearch() {
        if (this._debounceTimer) clearTimeout(this._debounceTimer)
        this._debounceTimer = setTimeout(() => this._onInputChange(), DEBOUNCE_MS)
      }

      _onInputChange() {
        const query = (this._els.input.value || '').trim()
        if (!query) {
          this._lastQuery = ''
          this._showInitialState()
          return
        }
        this._lastQuery = query
        if (this._showResultsPreview) {
          this._doSearch(query)
        }
      }

      _showInitialState() {
        this._hideAll()
        ++this._searchSeq
        this._renderRecent()
        if (this._showFeatured) this._showFeaturedSection()
      }

      _renderRecent() {
        if (!this._showRecent) return
        const els = this._els
        const api = searchApi()
        if (!api) return

        const recent = api.getRecent()
        if (!recent || recent.length === 0) return

        const clearHtml = this._showClearAll
          ? `<button class="sai-dt61y5va__recent-clear" type="button">Clear</button>`
          : ''
        const showDel = this._showIndividualDelete
        const items = recent
          .slice(0, this._recentMaxItems)
          .map((q) => buildRecentItem(q, showDel))
          .join('')
        const style = this._recentTagStyle
        els.recent.innerHTML = `<div class="sai-dt61y5va__recent-header"><span class="sai-dt61y5va__section-title sai-dt61y5va__recent-title">Recent searches</span>${clearHtml}</div><ul class="sai-dt61y5va__recent-list sai-dt61y5va__recent-list--${style}">${items}</ul>`

        els.recent.hidden = false

        const clearBtn = els.recent.querySelector('.sai-dt61y5va__recent-clear')
        if (clearBtn) {
          clearBtn.addEventListener(
            'click',
            () => {
              api.clearRecent()
              els.recent.hidden = true
            },
            { once: true },
          )
        }

        for (const delBtn of els.recent.querySelectorAll('.sai-dt61y5va__recent-delete')) {
          delBtn.addEventListener('click', (e) => {
            e.stopPropagation()
            const q = delBtn.getAttribute('data-delete-query')
            if (q && api.removeRecent) {
              api.removeRecent(q)
            } else if (q) {
              const all = api.getRecent() || []
              api.clearRecent()
              for (const r of all.filter((r) => r !== q)) api.addRecent(r)
            }
            delBtn.closest('.sai-dt61y5va__recent-item').remove()
            if (!els.recent.querySelector('.sai-dt61y5va__recent-item')) {
              els.recent.hidden = true
            }
          })
        }

        for (const item of els.recent.querySelectorAll('.sai-dt61y5va__recent-item')) {
          item.addEventListener('click', () => {
            const q = item.getAttribute('data-query')
            if (q) {
              els.input.value = q
              if (els.clearBtn) els.clearBtn.hidden = false
              this._lastQuery = q
              this._doSearch(q)
            }
          })
        }
      }

      async _fetchFeaturedProducts() {
        if (this._featuredCache) return this._featuredCache
        if (this._featuredPromise) return this._featuredPromise
        this._featuredPromise = (async () => {
          try {
            const res = await fetch(
              `/collections/${this._featuredHandle}/products.json?limit=${this._featuredMaxItems}`,
            )
            if (!res.ok) return null
            const json = await res.json()
            this._featuredCache = json.products || []
            return this._featuredCache
          } catch (_) {
            return null
          } finally {
            this._featuredPromise = null
          }
        })()
        return this._featuredPromise
      }

      _featuredViewAllHref() {
        return this._featuredViewAllUrl || `/collections/${this._featuredHandle}`
      }

      _featuredScrollClass() {
        const base = 'sai-dt61y5va__featured-scroll'
        return this._featuredLayout === 'scroll' ? `${base} ${base}--scroll` : base
      }

      async _showFeaturedSection() {
        const els = this._els

        if (this._featuredCache) {
          this._renderFeaturedProducts(this._featuredCache)
          return
        }

        els.featured.innerHTML = `<div class="sai-dt61y5va__featured-header"><span class="sai-dt61y5va__section-title sai-dt61y5va__featured-title">${this._featuredTitle}</span><a class="sai-dt61y5va__featured-clear" href="${this._featuredViewAllHref()}">View all</a></div><div class="${this._featuredScrollClass()}">${buildSkeletonGrid(this._featuredMaxItems, 'grid')}</div>`
        els.featured.hidden = false

        const products = await this._fetchFeaturedProducts()
        if (!products || products.length === 0 || this._lastQuery) return
        this._renderFeaturedProducts(products)
      }

      _renderFeaturedProducts(products) {
        const els = this._els
        const cards = products.slice(0, this._featuredMaxItems).map(buildFeaturedCard).join('')
        els.featured.innerHTML = `<div class="sai-dt61y5va__featured-header"><span class="sai-dt61y5va__section-title sai-dt61y5va__featured-title">${this._featuredTitle}</span><a class="sai-dt61y5va__featured-clear" href="${this._featuredViewAllHref()}">View all</a></div><div class="${this._featuredScrollClass()}">${cards}</div>`
        els.featured.hidden = false
      }

      _ensureTriggerPlaceholderOverlay() {
        if (this._triggerOverlay && this._triggerOverlayPrefix === this._placeholderPrefix) return
        if (this._triggerOverlay) {
          this._triggerOverlay.remove()
          this._triggerOverlay = null
          this._triggerSlot = null
        }
        const bar = this._els.triggerBar
        bar.querySelectorAll('.sai-dt61y5va__placeholder-anim').forEach(el => el.remove())
        bar.style.position = 'relative'
        const overlay = document.createElement('div')
        overlay.className = 'sai-dt61y5va__placeholder-anim'
        const prefixHtml = this._placeholderPrefix
          ? `<span class="sai-dt61y5va__placeholder-prefix">${this._placeholderPrefix}&nbsp;</span>`
          : ''
        overlay.innerHTML = `${prefixHtml}<span class="sai-dt61y5va__placeholder-slot"></span>`
        bar.insertBefore(overlay, this._els.triggerInput)
        this._triggerOverlay = overlay
        this._triggerOverlayPrefix = this._placeholderPrefix
        this._triggerSlot = overlay.querySelector('.sai-dt61y5va__placeholder-slot')
      }

      _startTriggerPlaceholderAnim() {
        if (this._placeholderSuggestions.length === 0) return
        this._stopTriggerPlaceholderAnim()
        this._ensureTriggerPlaceholderOverlay()
        this._els.triggerInput.placeholder = ''
        this._triggerOverlay.hidden = false
        this._triggerIdx = 0
        if (this._placeholderAnimation === 'typewriter') {
          this._typewriterTrigger()
        } else {
          this._rotateTrigger()
        }
      }

      _stopTriggerPlaceholderAnim() {
        if (this._triggerAnimTimer) {
          clearInterval(this._triggerAnimTimer)
          this._triggerAnimTimer = null
        }
        if (this._triggerTwTimer) {
          clearTimeout(this._triggerTwTimer)
          this._triggerTwTimer = null
        }
        if (this._triggerOverlay) {
          this._triggerOverlay.hidden = true
        }
      }

      _rotateTrigger() {
        const slot = this._triggerSlot
        const suggestions = this._placeholderSuggestions
        if (!slot || suggestions.length === 0) return

        const showWord = (immediate) => {
          if (this._els.triggerInput.value) {
            this._triggerOverlay.hidden = true
            return
          }
          this._triggerOverlay.hidden = false
          const word = suggestions[this._triggerIdx % suggestions.length]
          if (immediate) {
            slot.textContent = word
            slot.classList.remove('sai-dt61y5va__placeholder-slot--out')
            slot.classList.add('sai-dt61y5va__placeholder-slot--in')
            this._triggerIdx++
            return
          }
          slot.classList.remove(
            'sai-dt61y5va__placeholder-slot--in',
            'sai-dt61y5va__placeholder-slot--enter',
          )
          slot.classList.add('sai-dt61y5va__placeholder-slot--out')
          setTimeout(() => {
            slot.classList.remove('sai-dt61y5va__placeholder-slot--out')
            slot.classList.add('sai-dt61y5va__placeholder-slot--enter')
            slot.textContent = word
            void slot.offsetHeight
            slot.classList.remove('sai-dt61y5va__placeholder-slot--enter')
            slot.classList.add('sai-dt61y5va__placeholder-slot--in')
            this._triggerIdx++
          }, 400)
        }
        showWord(true)
        this._triggerAnimTimer = setInterval(() => showWord(false), 3000)
      }

      _typewriterTrigger() {
        const slot = this._triggerSlot
        const suggestions = this._placeholderSuggestions
        if (!slot || suggestions.length === 0) return

        let cursor = slot.querySelector('.sai-dt61y5va__placeholder-cursor')
        if (!cursor) {
          cursor = document.createElement('span')
          cursor.className = 'sai-dt61y5va__placeholder-cursor'
          slot.appendChild(cursor)
        }

        const TYPE_SPEED = 80
        const DELETE_SPEED = 40
        const PAUSE_AFTER_TYPE = 2000
        const PAUSE_AFTER_DELETE = 400

        const typeWord = () => {
          if (this._els.triggerInput.value) {
            this._triggerOverlay.hidden = true
            return
          }
          this._triggerOverlay.hidden = false
          const word = suggestions[this._triggerIdx % suggestions.length]
          let charIdx = 0
          const typeChar = () => {
            if (this._els.triggerInput.value) return
            slot.textContent = word.substring(0, charIdx + 1)
            slot.appendChild(cursor)
            charIdx++
            if (charIdx < word.length) {
              this._triggerTwTimer = setTimeout(typeChar, TYPE_SPEED)
            } else {
              this._triggerTwTimer = setTimeout(deleteWord, PAUSE_AFTER_TYPE)
            }
          }
          const deleteWord = () => {
            if (this._els.triggerInput.value) return
            slot.textContent = word.substring(0, charIdx)
            slot.appendChild(cursor)
            charIdx--
            if (charIdx > 0) {
              this._triggerTwTimer = setTimeout(deleteWord, DELETE_SPEED)
            } else {
              slot.textContent = ''
              slot.appendChild(cursor)
              this._triggerIdx++
              this._triggerTwTimer = setTimeout(typeWord, PAUSE_AFTER_DELETE)
            }
          }
          typeChar()
        }
        typeWord()
      }

      _ensurePlaceholderOverlay() {
        if (this._placeholderOverlay && this._placeholderOverlayPrefix === this._placeholderPrefix)
          return
        if (this._placeholderOverlay) {
          this._placeholderOverlay.remove()
          this._placeholderOverlay = null
          this._placeholderSlot = null
        }
        const bar = this._els.input.parentElement
        bar.querySelectorAll('.sai-dt61y5va__placeholder-anim').forEach(el => el.remove())
        bar.style.position = 'relative'
        const overlay = document.createElement('div')
        overlay.className = 'sai-dt61y5va__placeholder-anim'
        const prefixHtml = this._placeholderPrefix
          ? `<span class="sai-dt61y5va__placeholder-prefix">${this._placeholderPrefix}&nbsp;</span>`
          : ''
        overlay.innerHTML = `${prefixHtml}<span class="sai-dt61y5va__placeholder-slot"></span>`
        bar.insertBefore(overlay, this._els.input)
        this._placeholderOverlay = overlay
        this._placeholderOverlayPrefix = this._placeholderPrefix
        this._placeholderSlot = overlay.querySelector('.sai-dt61y5va__placeholder-slot')
      }

      _startPlaceholderAnim() {
        if (this._placeholderSuggestions.length === 0) return
        this._stopPlaceholderAnim()
        this._ensurePlaceholderOverlay()
        this._els.input.placeholder = ''
        this._placeholderOverlay.hidden = false
        this._placeholderIdx = 0
        if (this._placeholderAnimation === 'typewriter') {
          this._typewriterPlaceholder()
        } else {
          this._rotatePlaceholder()
        }
      }

      _stopPlaceholderAnim() {
        if (this._placeholderAnimTimer) {
          clearInterval(this._placeholderAnimTimer)
          this._placeholderAnimTimer = null
        }
        if (this._twTimer) {
          clearTimeout(this._twTimer)
          this._twTimer = null
        }
        if (this._placeholderOverlay) {
          this._placeholderOverlay.hidden = true
        }
      }

      _rotatePlaceholder() {
        const slot = this._placeholderSlot
        const suggestions = this._placeholderSuggestions
        if (!slot || suggestions.length === 0) return

        const showWord = (immediate) => {
          if (this._els.input.value) {
            this._placeholderOverlay.hidden = true
            return
          }
          this._placeholderOverlay.hidden = false
          const word = suggestions[this._placeholderIdx % suggestions.length]

          if (immediate) {
            slot.textContent = word
            slot.classList.remove('sai-dt61y5va__placeholder-slot--out')
            slot.classList.add('sai-dt61y5va__placeholder-slot--in')
            this._placeholderIdx++
            return
          }

          slot.classList.remove(
            'sai-dt61y5va__placeholder-slot--in',
            'sai-dt61y5va__placeholder-slot--enter',
          )
          slot.classList.add('sai-dt61y5va__placeholder-slot--out')

          setTimeout(() => {
            slot.classList.remove('sai-dt61y5va__placeholder-slot--out')
            slot.classList.add('sai-dt61y5va__placeholder-slot--enter')
            slot.textContent = word
            void slot.offsetHeight
            slot.classList.remove('sai-dt61y5va__placeholder-slot--enter')
            slot.classList.add('sai-dt61y5va__placeholder-slot--in')
            this._placeholderIdx++
          }, 400)
        }

        showWord(true)
        this._placeholderAnimTimer = setInterval(() => showWord(false), 3000)
      }

      _typewriterPlaceholder() {
        const slot = this._placeholderSlot
        const suggestions = this._placeholderSuggestions
        if (!slot || suggestions.length === 0) return

        let cursor = slot.querySelector('.sai-dt61y5va__placeholder-cursor')
        if (!cursor) {
          cursor = document.createElement('span')
          cursor.className = 'sai-dt61y5va__placeholder-cursor'
          slot.appendChild(cursor)
        }

        const TYPE_SPEED = 80
        const DELETE_SPEED = 40
        const PAUSE_AFTER_TYPE = 2000
        const PAUSE_AFTER_DELETE = 400

        const typeWord = () => {
          if (this._els.input.value) {
            this._placeholderOverlay.hidden = true
            return
          }
          this._placeholderOverlay.hidden = false
          const word = suggestions[this._placeholderIdx % suggestions.length]
          let charIdx = 0

          const typeChar = () => {
            if (this._els.input.value) return
            slot.textContent = word.substring(0, charIdx + 1)
            slot.appendChild(cursor)
            charIdx++
            if (charIdx < word.length) {
              this._twTimer = setTimeout(typeChar, TYPE_SPEED)
            } else {
              this._twTimer = setTimeout(deleteWord, PAUSE_AFTER_TYPE)
            }
          }

          const deleteWord = () => {
            if (this._els.input.value) return
            slot.textContent = word.substring(0, charIdx)
            slot.appendChild(cursor)
            charIdx--
            if (charIdx > 0) {
              this._twTimer = setTimeout(deleteWord, DELETE_SPEED)
            } else {
              slot.textContent = ''
              slot.appendChild(cursor)
              this._placeholderIdx++
              this._twTimer = setTimeout(typeWord, PAUSE_AFTER_DELETE)
            }
          }

          typeChar()
        }

        typeWord()
      }

      async _doSearch(query) {
        const els = this._els
        const api = searchApi()

        if (!api) {
          this._hideAll()
          els.noResults.textContent = 'Search is not available. Please try again later.'
          els.noResults.hidden = false
          return
        }

        this._hideAll()
        const searchId = ++this._searchSeq
        els.loadingGrid.className =
          this._resultsLayout === 'list'
            ? 'sai-dt61y5va__loading-grid sai-dt61y5va__loading-grid--list'
            : 'sai-dt61y5va__loading-grid'
        els.loadingGrid.innerHTML = buildSkeletonGrid(this._resultsCount, this._resultsLayout)
        els.loading.hidden = false

        const searchResult = await api.predictive(query, { hitsPerPage: this._resultsCount })

        if (searchResult === null || searchId !== this._searchSeq) return

        els.loading.hidden = true

        if (!searchResult.ok || !searchResult.data) {
          els.results.hidden = true
          els.noResults.textContent = 'Something went wrong. Try again.'
          els.noResults.hidden = false
          return
        }

        const products = searchResult.data.products || []
        const totalHits = searchResult.data.totalHits || 0

        if (products.length === 0) {
          els.results.hidden = true
          els.noResults.textContent = `No results found for "${query}". Try another search.`
          els.noResults.hidden = false
          if (this._showFeatured) this._showFeaturedSection()

          this._track(`${FEATURE_SLUG}:no_results`, { query })
          return
        }

        api.addRecent(query)

        els.grid.className =
          this._resultsLayout === 'list'
            ? 'sai-dt61y5va__grid sai-dt61y5va__grid--list'
            : 'sai-dt61y5va__grid'
        els.grid.innerHTML = products.map(buildProductCard).join('')
        els.viewAll.href = `${this._searchPageUrl}?q=${encodeURIComponent(query)}`
        els.viewAll.textContent = this._viewAllText
        els.viewAll.className =
          this._viewAllStyle === 'link'
            ? 'sai-dt61y5va__view-all sai-dt61y5va__view-all--link'
            : 'sai-dt61y5va__view-all'

        const viewAllWrap = els.results.querySelector('.sai-dt61y5va__view-all-wrap')
        if (viewAllWrap) viewAllWrap.hidden = totalHits <= products.length

        els.results.hidden = false

        this._track(`${FEATURE_SLUG}:results`, {
          query,
          result_count: products.length,
          total_hits: totalHits,
        })
      }

      applyVariant(content) {
        if (!this._els) return
        const placeholder = content?.placeholder || 'What are you looking for today?'
        this._els.input.placeholder = placeholder
        this._els.triggerInput.placeholder = placeholder
        this._placeholderPrefix = content?.placeholder_prefix ?? 'Search for'
        this._placeholderAnimation = content?.placeholder_animation || 'rotate'
        this._placeholderSuggestions = (content?.placeholder_suggestions || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
        this._searchPageUrl = content?.search_page_url || '/search'
        this._drawerDirection = content?.drawer_direction || 'top'
        this._drawerStyle = content?.drawer_style || 'fixed'
        this._searchIconPosition = content?.search_icon_position || 'left'
        this._showClearButton = content?.show_clear_button !== false
        this._autoFocus = content?.auto_focus !== false
        this._showResultsPreview = content?.show_results_preview !== false
        this._resultsCount = content?.results_count || 8
        this._viewAllText = content?.view_all_text || 'View all'
        this._viewAllStyle = content?.view_all_style || 'link'
        this._resultsLayout = content?.results_layout || 'grid'
        this._showRecent = content?.show_recent !== false
        this._recentMaxItems = content?.recent_max_items || 5
        this._showClearAll = content?.show_clear_all !== false
        this._showIndividualDelete = content?.show_individual_delete !== false
        this._recentTagStyle = content?.recent_tag_style || 'pill'
        this._showFeatured = content?.show_featured !== false
        this._featuredTitle = content?.featured_title || 'Featured Products'
        const newHandle = content?.featured_handle || 'all'
        if (newHandle !== this._featuredHandle) {
          this._featuredHandle = newHandle
          this._featuredCache = null
        }
        this._featuredMaxItems = content?.featured_max_items || 8
        this._featuredViewAllUrl = content?.featured_view_all_url || ''
        this._featuredLayout = content?.featured_layout || 'grid'

        if (content?.icon_position) {
          this._els.triggerBar.setAttribute('data-icon-position', content.icon_position)
        }

        if (!this._isOpen) this._startTriggerPlaceholderAnim()
      }
    }

    customElements.define(TAG, SaiSearchDrawerBar)
  }

  function bindAllContainers() {
    const api = window.__spectrumAi?.snippet
    const containers = document.querySelectorAll(
      `[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`,
    )

    if (!api?.bind) return

    for (const node of containers) {
      const handles = api.bind(node, ({ variant }) => {
        if (!variant?.content) return
        const root = node.querySelector(TAG)
        if (root && typeof root.applyVariant === 'function') {
          root.applyVariant(variant.content)
        }
      })
      const root = node.querySelector(TAG)
      if (root && handles?.track && typeof root.setAnalytics === 'function') {
        root.setAnalytics(handles.track)
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAllContainers, { once: true })
  } else {
    bindAllContainers()
  }
})()
